/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import {
  AssetOptionType,
  BundleOption,
  CollectionApi,
  Constants,
  DestructuredDuration,
  Ducks,
  DurationUtils,
  Folder,
  FolderPurchaseOption,
  FolderType,
  PpvModule,
  ProductPrice,
  ProductPriceApi,
  ProductPriceStatus,
  PurchaseOptionType,
  SubscriptionOption,
} from '@wit/vtv-x-foundation-lib';
import { SagaIterator } from 'redux-saga';
import { call } from 'redux-saga/effects';

export function checkIsRental(fullLifeCycle: DestructuredDuration): boolean {
  const rentalDestructuredDuration = DurationUtils.toDestructuredDuration(Constants.RENTED_ENTITLEMENT_DURATION);
  if (rentalDestructuredDuration) {
    const maxRentalDuration = DurationUtils.destructuredDurationtoMilliseconds(rentalDestructuredDuration);
    const fullLifeCycleDuration = DurationUtils.destructuredDurationtoMilliseconds(fullLifeCycle);
    return maxRentalDuration > fullLifeCycleDuration;
  }
  return false;
}

function* getSubscriptionPrices(folders: Folder[], showSubscribed: boolean): SagaIterator<ProductPrice[]> {
  const subscriptionsFolders: Folder[] = folders.filter((folder) => folder.folderType === FolderType.SUBSCRIPTION);
  const subscriptionsIds: string[] = [];
  subscriptionsFolders.forEach((folder) => {
    if (folder.productId !== undefined) {
      subscriptionsIds.push(folder.productId);
    }
  });

  let subscriptionsPrices: ProductPrice[] = [];
  if (subscriptionsIds.length) {
    subscriptionsPrices = yield Ducks.Api.callApi(ProductPriceApi.listBySubscriptionIds, subscriptionsIds);
  }

  const allowedSubscriptionPrices = subscriptionsPrices.filter(
    ({ status }) =>
      status === ProductPriceStatus.NOT_PURCHASED ||
      (showSubscribed && (status === ProductPriceStatus.PURCHASED || status === ProductPriceStatus.FREE))
  );

  return allowedSubscriptionPrices;
}

function* getBundlePrices(folders: Folder[], showRented: boolean): SagaIterator<ProductPrice[]> {
  const bundleFolders: Folder[] = folders.filter((folder) => folder.folderType === FolderType.BUNDLE);
  const bundleIds: string[] = [];
  bundleFolders.forEach((folder) => {
    if (folder.productId !== undefined) {
      bundleIds.push(folder.productId);
    }
  });

  let bundlePrices: ProductPrice[] = [];
  if (bundleIds.length) {
    bundlePrices = yield Ducks.Api.callApi(ProductPriceApi.listByCollectionIds, bundleIds);
  }

  const allowedBundlePrices = bundlePrices.filter(
    ({ status }) =>
      status === ProductPriceStatus.NOT_PURCHASED ||
      (showRented && (status === ProductPriceStatus.PURCHASED || status === ProductPriceStatus.FREE))
  );

  return allowedBundlePrices;
}

function* getBundleModules(folders: Folder[]): SagaIterator<PpvModule[]> {
  const bundleFolders: Folder[] = folders.filter((folder) => folder.folderType === FolderType.BUNDLE);
  const bundleIds: string[] = [];
  bundleFolders.forEach((folder) => {
    if (folder.productId !== undefined) {
      bundleIds.push(folder.productId);
    }
  });

  const ppvModules: PpvModule[] = yield Ducks.Api.callApi(CollectionApi.getCollectionsById, bundleIds);
  return ppvModules;
}

function buildFolderPurchaseOption(
  folder: Folder,
  productPrice: ProductPrice,
  ppvModule?: PpvModule
): FolderPurchaseOption {
  const subscriptionOption: SubscriptionOption = {
    key: `${AssetOptionType.SUBSCRIPTION}-${folder.productId}`,
    type: AssetOptionType.SUBSCRIPTION,
    title: '',
    productPrice,
    asset: folder,
    ppvModule,
  };
  if (folder.folderType === FolderType.BUNDLE) {
    const isRental = ppvModule && checkIsRental(ppvModule.fullLifeCycle);
    const bundleOption: BundleOption = {
      ...subscriptionOption,
      type: AssetOptionType.BUNDLE,
      purchaseOptionType: isRental || !ppvModule ? PurchaseOptionType.RENT : PurchaseOptionType.BUY,
    };
    return bundleOption;
  }
  return subscriptionOption;
}

function* getOptions(
  folders: Folder[],
  showSubscribed = false,
  showRented = false
): SagaIterator<FolderPurchaseOption[]> {
  const subscriptionsPrices: ProductPrice[] = yield call(getSubscriptionPrices, folders, showSubscribed);
  const bundlePrices: ProductPrice[] = yield call(getBundlePrices, folders, showRented);

  const allowedProductPrices: ProductPrice[] = [...subscriptionsPrices, ...bundlePrices];

  if (allowedProductPrices.length === 0) {
    return [];
  }

  const ppvModules: PpvModule[] = yield call(getBundleModules, folders);

  const options: FolderPurchaseOption[] = [];

  folders.forEach((folder) => {
    const option: FolderPurchaseOption[] = [];
    const productPrice = allowedProductPrices.find((price) => price.productId === folder.productId);
    const ppvModule = ppvModules.find((module) => `${module.id}` === folder.productId);

    if (productPrice) {
      option.push(buildFolderPurchaseOption(folder, productPrice, ppvModule));
    }
    if (option !== undefined) {
      options.push(...option);
    }
  });

  return options;
}

export default {
  getOptions,
};
